/**
 * EmployerSalaryBands — curated per-firm salary bands.
 *
 * Fetches /api/employers/{slug}/salary-bands and renders a table
 * per location (London / US / etc.) with role, base pay and total-
 * comp max. Bands are converted to the viewer's currency via the
 * CurrencyContext and the original figure is shown in parentheses.
 * The highlighted flagship role (NQ / Analyst 1 / L4) sits bold.
 *
 * Silently omits the whole section when we haven't curated data
 * for a firm yet (returns null so the EmployerDetail flow just
 * shows the cohort chart, unchanged).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { CheckCircle2, ExternalLink, ShieldCheck, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

const SECTOR_ACCENT = {
  law:          { label: "Law · London",       chip: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  banking:      { label: "Investment Banking", chip: "border-sky-300 bg-sky-50 text-sky-800" },
  hedge_fund:   { label: "Hedge Fund",         chip: "border-teal-300 bg-teal-50 text-teal-800" },
  prop_trading: { label: "Prop Trading",       chip: "border-rose-300 bg-rose-50 text-rose-800" },
  consulting:   { label: "Consulting",         chip: "border-violet-300 bg-violet-50 text-violet-800" },
  tech:         { label: "Big Tech",           chip: "border-orange-300 bg-orange-50 text-orange-800" },
  public:       { label: "Public sector",      chip: "border-stone-300 bg-stone-50 text-stone-700" },
  // Feb 2026 additions — cover every sector on the sector-native
  // firms so the fallback ("Consulting") no longer mislabels e.g.
  // Ericsson as a consultancy.
  telecoms:     { label: "Telecoms",           chip: "border-cyan-300 bg-cyan-50 text-cyan-800" },
  fmcg:         { label: "FMCG",               chip: "border-lime-300 bg-lime-50 text-lime-800" },
  engineering:  { label: "Engineering",        chip: "border-slate-300 bg-slate-50 text-slate-700" },
  aerospace:    { label: "Aerospace",          chip: "border-indigo-300 bg-indigo-50 text-indigo-800" },
  automotive:   { label: "Automotive",         chip: "border-red-300 bg-red-50 text-red-800" },
  travel:       { label: "Travel & Hospitality", chip: "border-teal-300 bg-teal-50 text-teal-800" },
  media:        { label: "Media",              chip: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800" },
  semiconductors: { label: "Semiconductors",   chip: "border-purple-300 bg-purple-50 text-purple-800" },
  "consumer-engineering": { label: "Consumer engineering", chip: "border-neutral-300 bg-neutral-50 text-neutral-700" },
  consumer:     { label: "Consumer",           chip: "border-amber-300 bg-amber-50 text-amber-800" },
  ngo:          { label: "NGO",                chip: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  health:       { label: "Health",             chip: "border-rose-300 bg-rose-50 text-rose-800" },
  healthcare:   { label: "Healthcare",         chip: "border-rose-300 bg-rose-50 text-rose-800" },
  pharma:       { label: "Pharma",             chip: "border-pink-300 bg-pink-50 text-pink-800" },
  "life-sciences": { label: "Life Sciences",   chip: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  energy:       { label: "Energy",             chip: "border-yellow-300 bg-yellow-50 text-yellow-800" },
  education:    { label: "Education",          chip: "border-blue-300 bg-blue-50 text-blue-800" },
  arts:         { label: "Arts & Culture",     chip: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800" },
  finance:      { label: "Finance",            chip: "border-sky-300 bg-sky-50 text-sky-800" },
  insurance:    { label: "Insurance",          chip: "border-blue-300 bg-blue-50 text-blue-800" },
  retail:       { label: "Retail",             chip: "border-orange-300 bg-orange-50 text-orange-800" },
  fashion:      { label: "Fashion & Luxury",   chip: "border-rose-300 bg-rose-50 text-rose-800" },
};

export default function EmployerSalaryBands({ slug, employerName }) {
  const { formatFrom } = useCurrency();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Firm-submitted, editor-approved bands (Tier-2 beta). Fetched from
  // the public claim-status endpoint so a single round-trip powers
  // both the "Verified Partner" chip and the "Officially supplied by"
  // firm-submitted salary rows.
  const [firmSubmitted, setFirmSubmitted] = useState({ bands: [], firm_name: null });

  useEffect(() => {
    if (!slug) return;
    setLoaded(false);
    setData(null);
    setFirmSubmitted({ bands: [], firm_name: null });
    Promise.all([
      api.get(`/employers/${encodeURIComponent(slug)}/salary-bands`).catch(() => ({ data: { available: false } })),
      api.get(`/firm-claims/status/${encodeURIComponent(slug)}`).catch(() => ({ data: null })),
    ])
      .then(([bandsRes, claimRes]) => {
        setData(bandsRes.data);
        const cd = claimRes?.data || {};
        setFirmSubmitted({
          bands: cd.firm_submitted_bands || [],
          firm_name: cd.firm_name || null,
        });
      })
      .finally(() => setLoaded(true));
  }, [slug]);

  if (!loaded) return null;
  // Show the wrapper if EITHER curated bands or firm-submitted bands exist.
  const hasCurated = !!data?.available;
  const hasFirmSubmitted = (firmSubmitted.bands || []).length > 0;
  if (!hasCurated && !hasFirmSubmitted) return null;

  const accent = SECTOR_ACCENT[data?.sector] || SECTOR_ACCENT.consulting;
  // Format the source `as_of` (typically "2025-Q4" or ISO date) into the
  // vintage families expect to see on a benchmark: "Q4 2025", "Jan 2025",
  // "2025". Falls back gracefully when the field is missing.
  const vintageLabel = (() => {
    const raw = data?.freshness?.as_of;
    if (!raw) return null;
    const s = String(raw).trim();
    // ISO date? Show "MMM YYYY".
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const dt = new Date(s + "T00:00:00Z");
      return dt.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
    }
    // "2025-01" — pinpoint month.
    if (/^\d{4}-\d{2}$/.test(s)) {
      const [y, m] = s.split("-");
      const dt = new Date(Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, 1));
      return dt.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
    }
    // "2025-Q4" — already quarter-formatted, flip to "Q4 2025".
    const qMatch = s.match(/^(\d{4})-Q([1-4])$/i);
    if (qMatch) return `Q${qMatch[2]} ${qMatch[1]}`;
    return s;
  })();

  const isBenchmark = data?.data_confidence === "sector_benchmark";

  return (
    <>
      {hasCurated && (
      <section
      data-testid="employer-salary-bands"
      className={`mt-6 rounded-lg border ${isBenchmark ? "border-amber-200 bg-amber-50/30" : "border-emerald-200 bg-emerald-50/30"} p-5 lg:p-6`}
    >
      <header className="flex items-start gap-3 mb-4">
        <div className={`mt-0.5 shrink-0 rounded-full ${isBenchmark ? "bg-amber-100" : "bg-emerald-100"} p-1.5`}>
          <ShieldCheck className={`h-4 w-4 ${isBenchmark ? "text-amber-700" : "text-emerald-700"}`} />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${isBenchmark ? "text-amber-900" : "text-emerald-900"}`}>
              {isBenchmark ? "Sector-benchmark salary bands" : "Curated firm-published salary bands"}
            </div>
            {isBenchmark && (
              <TooltipProvider delayDuration={100} disableHoverableContent>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid="salary-bands-benchmark-chip"
                      className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border border-amber-400 bg-amber-50 text-amber-900 px-2 py-0.5 cursor-help hover:bg-amber-100 transition"
                      tabIndex={0}
                    >
                      Unverified · sector benchmark
                      <Info className="h-2.5 w-2.5" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="max-w-[320px] bg-stone-900 text-stone-50 text-[11px] leading-snug px-3 py-2"
                    data-testid="salary-bands-benchmark-tooltip"
                  >
                    <div className="font-semibold text-amber-200 mb-1">What "sector benchmark" means</div>
                    <p>
                      This firm hasn't published salary bands, so these figures are the sector median
                      pulled from Levels.fyi + Glassdoor for comparable roles at peer firms — not
                      audited numbers from the firm itself.
                    </p>
                    <p className="mt-1.5">
                      Treat them as a directional guide. Always cross-check the firm's own careers
                      page or a current offer letter before making a decision.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span
              className={`text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 ${accent.chip}`}
            >
              {accent.label}
            </span>
            {vintageLabel && (
              <span
                data-testid="salary-bands-vintage-chip"
                title={
                  data.freshness?.is_stale
                    ? `These numbers were verified in ${vintageLabel}. They're now overdue for our 90-day refresh — check the sources below before relying on them.`
                    : `These numbers were verified in ${vintageLabel} against each source below. Next refresh scheduled ${data.freshness?.next_refresh_at || "shortly"}.`
                }
                className={
                  "inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 " +
                  (data.freshness?.is_stale
                    ? "border-amber-400 bg-amber-50 text-amber-900"
                    : "border-emerald-400 bg-white text-emerald-800")
                }
              >
                <ShieldCheck className="h-2.5 w-2.5" aria-hidden />
                {data.freshness?.is_stale
                  ? `Refresh due · Verified ${vintageLabel}`
                  : `Verified ${vintageLabel}`}
              </span>
            )}
          </div>
          <p className="mt-1 text-[13px] text-stone-700 max-w-2xl leading-relaxed">
            Real pay bands as published by{" "}
            <span className="font-medium">{data.firm_name || employerName}</span>{" "}
            (and cross-checked against the sources below). Unlike the cohort
            chart further down, these are the firm's <em>own</em> role-by-role
            salaries.
          </p>
        </div>
      </header>

      <div className="space-y-4">
        {data.locations.map((loc, i) => (
          <BandTable
            key={i}
            location={loc}
            index={i}
            formatFrom={formatFrom}
          />
        ))}
      </div>

      {/* Sources */}
      <div className="mt-4 pt-3 border-t border-emerald-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-[11px] text-stone-600 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-stone-700">Sources:</span>
          {(data.sources || []).map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`salary-bands-source-${i}`}
              className="inline-flex items-center gap-1 underline hover:text-[#4C1D95]"
            >
              {s.name}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          ))}
          {/* The vintage chip in the header carries the "Verified {as_of}"
              signal; the footer just anchors the sources and a next-refresh
              hint so the trust signal doesn't double up. */}
          {data.freshness && data.freshness.next_refresh_at && !data.freshness.is_stale && (
            <span
              data-testid="salary-bands-freshness"
              className="text-[10px] font-underground-sc tracking-wider text-stone-500"
            >
              · next refresh {data.freshness.next_refresh_at}
            </span>
          )}
        </div>
        {data.note && (
          <div className="text-[11px] text-stone-500 italic max-w-md sm:text-right">
            {data.note}
          </div>
        )}
      </div>
    </section>
      )}

      {/* Firm-submitted bands (Tier-2 beta) — rendered as a separate
          panel with an "Officially supplied by" chip so families can
          tell at a glance that this row came from the firm itself,
          not our editorial team or a scraped benchmark. */}
      {hasFirmSubmitted && (
        <FirmSubmittedBandsPanel
          bands={firmSubmitted.bands}
          firmName={firmSubmitted.firm_name || data?.firm_name || employerName}
          formatFrom={formatFrom}
        />
      )}
    </>
  );
}

function FirmSubmittedBandsPanel({ bands, firmName, formatFrom }) {
  return (
    <section
      data-testid="firm-submitted-bands-panel"
      className="mt-6 rounded-lg border border-[#4C1D95]/30 bg-[#F5F0FF]/40 p-5 lg:p-6"
    >
      <header className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 shrink-0 rounded-full bg-[#F5F0FF] p-1.5">
          <ShieldCheck className="h-4 w-4 text-[#4C1D95]" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4C1D95]">
              Firm-published salary bands
            </div>
            <span
              data-testid="firm-submitted-bands-officially-chip"
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border border-[#4C1D95]/40 bg-white text-[#4C1D95] px-2 py-0.5"
            >
              <ShieldCheck className="h-2.5 w-2.5" />
              Officially supplied by {firmName}
            </span>
          </div>
          <p className="mt-1 text-[13px] text-stone-700 max-w-2xl leading-relaxed">
            Submitted directly by {firmName}'s team and reviewed by our editorial
            board before publishing. Each entry links to a public source (careers
            page, press release, or offer letter).
          </p>
        </div>
      </header>

      <ul className="space-y-2" data-testid="firm-submitted-bands-list">
        {bands.map((b) => (
          <li
            key={b.id}
            className="rounded-md border border-stone-200 bg-white p-3 flex flex-wrap items-baseline justify-between gap-2"
            data-testid={`firm-submitted-band-row-${b.id}`}
          >
            <div>
              <div className="text-sm font-semibold text-stone-900">{b.role}</div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                {b.location} · Source:{" "}
                <a
                  href={b.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                >
                  view <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono tabular-nums text-sm text-stone-900">
                {formatFrom(b.base, b.currency)}
              </div>
              {b.total_comp && (
                <div className="text-[10px] font-mono text-stone-500 mt-0.5">
                  TC max {formatFrom(b.total_comp, b.currency)}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BandTable({ location, index, formatFrom }) {
  const { location_label, currency, bands } = location;
  return (
    <div
      data-testid={`salary-bands-location-${index}`}
      className="rounded-md border border-stone-200 bg-white overflow-hidden"
    >
      <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
        <div className="text-[11px] font-underground-sc tracking-wider text-stone-600 font-semibold">
          {location_label}
        </div>
        <div className="text-[10px] font-mono text-stone-400">
          native currency: {currency}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] font-underground-sc tracking-wider text-stone-500">
          <tr>
            <th className="text-left px-4 py-2 font-normal">Role</th>
            <th className="text-right px-4 py-2 font-normal">Base</th>
            <th className="text-right px-4 py-2 font-normal">Total comp (max)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {bands.map((b, i) => (
            <BandRow
              key={i}
              band={b}
              index={i}
              currency={currency}
              formatFrom={formatFrom}
              locationIndex={index}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BandRow({ band, index, currency, formatFrom, locationIndex }) {
  const isFlag = !!band.highlight;
  const rowCls = isFlag
    ? "bg-emerald-50/40"
    : "";
  const cellCls = isFlag ? "font-semibold text-emerald-900" : "text-stone-700";
  const testid = `salary-bands-loc${locationIndex}-row${index}`;

  const nativeFmt = (v) =>
    v == null
      ? null
      : new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency,
          maximumFractionDigits: 0,
        }).format(v);

  return (
    <tr data-testid={testid} className={rowCls}>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          {isFlag && (
            <CheckCircle2
              className="h-3 w-3 text-emerald-700 shrink-0"
              aria-label="Flagship graduate role"
            />
          )}
          <span className={cellCls}>{band.role}</span>
          {isFlag && (
            <span className="text-[9px] font-underground-sc tracking-wider text-emerald-700 ml-1">
              flagship
            </span>
          )}
        </div>
      </td>
      <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${cellCls}`}>
        {formatFrom(band.base_annual, currency)}
        <div className="text-[10px] text-stone-400 font-normal">
          ({nativeFmt(band.base_annual)})
        </div>
      </td>
      <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${cellCls}`}>
        {band.total_max_annual != null
          ? formatFrom(band.total_max_annual, currency)
          : "—"}
        {band.total_max_annual != null && (
          <div className="text-[10px] text-stone-400 font-normal">
            ({nativeFmt(band.total_max_annual)})
          </div>
        )}
      </td>
    </tr>
  );
}
